/**
 * The router, built from the generated tree.
 *
 * `ARCHITECTURE.md` §Frontend: "TanStack Router, file-based, typed params and
 * search." `src/routes/` is the tree and `src/routeTree.gen.ts` is what the Vite
 * plugin writes from it; that file is committed, because `pnpm typecheck` runs
 * before `pnpm build` in `ci:local` and a type-check cannot generate it.
 * `src/routes/routes.test.ts` fails if the two have drifted.
 *
 * `createAppRouter` takes its dependencies rather than importing them, so a test
 * can build the same router over a fake auth client and its own query cache and
 * drive it with a memory history.
 */

import { createRouter, type RouterHistory } from '@tanstack/react-router'

import { RouteError } from './errors/RouteError.js'
import { routeTree } from './routeTree.gen.js'
import type { RouterContext } from './router-context.js'

export type AppRouterOptions = RouterContext & {
  /** A memory history in a test; the browser's own in `main.tsx`. */
  readonly history?: RouterHistory
}

export function createAppRouter({ queryClient, auth, history }: AppRouterOptions) {
  return createRouter({
    routeTree,
    context: { queryClient, auth },
    ...(history === undefined ? {} : { history }),
    /**
     * Never show a spinner for a load that is about to finish. TanStack's own
     * advice, and it is what stops a fast navigation flashing.
     */
    defaultPendingMs: 200,
    defaultPendingMinMs: 300,
    /**
     * An error boundary on **every** route, not just the root.
     *
     * TanStack installs one only where `route.options.errorComponent ??
     * router.options.defaultErrorComponent` is truthy (`Match.js`); everywhere
     * else it is a fragment and the throw goes up. With the root route alone
     * naming one, a page under `/app` that failed replaced the whole tree —
     * sidebar, topbar and all — with a bare error panel and no way to navigate.
     * With the default, the innermost boundary is the failing page's own route,
     * so its shell stays up and there is somewhere to go next.
     */
    defaultErrorComponent: RouteError,
    scrollRestoration: true,
  })
}

export type AppRouter = ReturnType<typeof createAppRouter>

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter
  }
}
